import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-checkbox/ix-checkbox.js';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { property, state, query } from 'lit/decorators.js';

import { IIxTreeNode } from './IIxTreeNode.js';
import { IxTreeStyles } from './ix-tree-styles.js';
import type { Appearance } from './IxTree.js';
import { IxTreeTestIds as TestIds } from './constants/IxTreeTestIds.js';

export class IxTreeNode extends LitElement {
  static readonly styles = [IxTreeStyles];

  @property({ type: Boolean }) isActiveTreeNode: boolean = false;

  @property({ type: Boolean }) isExpanded: boolean = false;

  @property({ type: Boolean }) isSelected: boolean = false;

  @property({ type: Number }) level: number = 0;

  @property({ type: Number }) posInSet: number = 1;

  @property({ type: Number }) setSize: number = 1;

  @property({ type: Object }) node!: IIxTreeNode;

  @property({ type: Boolean }) checkboxEnabled: boolean = false;

  @property({ type: String }) appearance: Appearance = 'default';

  @property({ type: Boolean }) hasIcon: boolean = false;

  @property({ type: Boolean }) removeChevron: boolean = false;

  @property({ type: Boolean }) allowMultiLine: boolean = false;

  @property({ type: Boolean }) scrollToActive: boolean = false;

  @property({ type: Boolean }) disabled: boolean = false;

  @property({ type: Boolean }) selectNodeOnClick: boolean = false;

  @state() private _hasScrolledToActive: boolean = false;

  @query('.tree-node-container') private _treeNodeContainer!: HTMLElement;

  private hasChildren() {
    return this.node.children !== undefined && this.node.children.length > 0;
  }

  private getChevron(isDisabled: boolean): TemplateResult<1> | typeof nothing {
    const icon = this.isExpanded ? 'arrow_drop_down' : 'arrow_right';
    const isChevronDisabled = isDisabled || !this.hasChildren();
    // The chevron is a real, focusable button (it handles Enter/Space), so it
    // must have an accessible name rather than rendering as an empty button in
    // the accessibility tree.
    const chevronLabel = this.isExpanded
      ? `Collapse ${this.node.label}`
      : `Expand ${this.node.label}`;
    return html`<div
      style=${`${this.hasChildren() ? '' : 'visibility:hidden'}`}
    >
      <ix-icon-button
        data-testid="${TestIds.CHEVRON_TOGGLE(this.node.id)}"
        @click=${(event: MouseEvent) => this.onChevronClick(event, this.node)}
        @keydown=${(event: KeyboardEvent) =>
          this.onChevronKeyDown(event, this.node)}
        icon=${icon}
        label=${chevronLabel}
        class="chevron"
        test-id="tree-node-expand-toggle"
        ?disabled=${isChevronDisabled}
        tabindex="-1"
      >
      </ix-icon-button>
    </div>`;
  }

  private static isEventFromChevron(event: KeyboardEvent): boolean {
    return event
      .composedPath()
      .some(
        pathItem =>
          pathItem instanceof HTMLElement &&
          pathItem.tagName === 'IX-ICON-BUTTON'
      );
  }

  private onChevronKeyDown(event: KeyboardEvent, node: IIxTreeNode): void {
    if (this.disabled || node.disabled || !this.hasChildren()) {
      return;
    }

    const { key } = event;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.onNodeToggle(node);
    }
  }

  private onChevronClick(event: MouseEvent, node: IIxTreeNode): void {
    // Prevent the click from bubbling to the treeitem container and triggering
    // node selection; the chevron only toggles expansion.
    event.stopPropagation();

    if (this.disabled || node.disabled || !this.hasChildren()) {
      return;
    }

    this.onNodeToggle(node);
  }

  private renderCheckbox() {
    if (this.node.fixed) {
      return html` <ix-icon class="inherited">check</ix-icon>`;
    }

    if (this.node.disabled) {
      return html` <ix-icon class="inherited">arrow_drop_up</ix-icon>`;
    }

    return html`<ix-checkbox
      data-testid="${TestIds.CHECKBOX(this.node.id)}"
      class="item-checkbox"
      .checked=${this.node.checked}
      .indeterminate=${this.node.indeterminate}
      tabindex="-1"
    ></ix-checkbox>`;
  }

  private onNodeClick(node: IIxTreeNode): void {
    const event = new CustomEvent('on-tree-node-click', {
      detail: {
        message: {
          node,
          id: node.id,
          label: node.label,
          parentId: node.parentId,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private onNodeToggle(node: IIxTreeNode): void {
    const event = new CustomEvent('on-tree-node-toggle', {
      detail: {
        message: {
          node,
          id: node.id,
          label: node.label,
          parentId: node.parentId,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private onNodeCheck(node: IIxTreeNode): void {
    /* eslint-disable no-param-reassign */
    if (node.checked) {
      node.checked = false;
      node.indeterminate = false;
    } else {
      node.checked = true;
      node.indeterminate = false;
    }
    const event = new CustomEvent('on-tree-node-check', {
      detail: {
        message: {
          node,
          id: node.id,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private handleNodeClick(event: Event, node: IIxTreeNode): void {
    // A node's slotted children render inside this node's `.tree-node-container`,
    // so a click on a child bubbles (composed) up to every ancestor container.
    // Stop it here so only the clicked node emits a selection/check event.
    event.stopPropagation();

    if (this.disabled || node.disabled) {
      return;
    }

    if (this.selectNodeOnClick) {
      this.onNodeCheck(node);
      return;
    }

    if (this.checkboxEnabled) {
      this.onNodeCheck(node);
    } else {
      this.onNodeClick(node);
    }
  }

  private handleNodeKeyDown(event: KeyboardEvent, node: IIxTreeNode): void {
    if (this.disabled || node.disabled) {
      return;
    }

    if (IxTreeNode.isEventFromChevron(event)) {
      return;
    }

    const { key } = event;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.handleNodeClick(event, node);
    }
  }

  private onNodeFocus(node: IIxTreeNode): void {
    this.dispatchEvent(
      new CustomEvent('on-tree-node-focus', {
        detail: {
          message: {
            node,
            id: node.id,
          },
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  protected render(): TemplateResult<1> {
    const selectedClass = this.isSelected ? 'selected' : '';
    const isDisabled = this.disabled || this.node.disabled;
    const disabledClass = isDisabled ? 'disabled' : '';
    const cursorClass = isDisabled ? 'cursor-default' : 'cursor-pointer';
    const icon = this.node.icon ? this.node.icon : 'account_box';
    const hasChildren = this.hasChildren();

    // The element that carries `role="treeitem"` must be the same element that
    // receives keyboard focus. If focus lands on a role-less element, NVDA stays
    // in browse mode and never forwards arrow keys to the tree's keydown handler.
    return html`<div
      ?active-tree-node=${this.isActiveTreeNode}
      class="tree-node-container"
      data-id=${this.node.id}
      data-testid="${TestIds.CONTAINER(this.node.id)}"
      role="treeitem"
      tabindex=${this.isActiveTreeNode ? '0' : '-1'}
      aria-label=${this.node.label}
      aria-level=${this.level + 1}
      aria-posinset=${this.posInSet}
      aria-setsize=${this.setSize}
      aria-selected=${this.isSelected}
      aria-disabled=${isDisabled}
      aria-expanded=${hasChildren ? this.isExpanded : nothing}
      aria-checked=${this.checkboxEnabled ? !!this.node.checked : nothing}
      @click=${(event: MouseEvent) => this.handleNodeClick(event, this.node)}
      @keydown=${(event: KeyboardEvent) =>
        this.handleNodeKeyDown(event, this.node)}
      @focus=${() => this.onNodeFocus(this.node)}
    >
      <div
        class="wrapper ${selectedClass} ${cursorClass} ${disabledClass}"
        style="padding-left: calc(var(--ix-tree-icon-size) * ${this.level})"
      >
        ${!this.removeChevron ? this.getChevron(isDisabled ?? false) : nothing}
        <div class="tree-node-content">
          ${this.checkboxEnabled ? this.renderCheckbox() : nothing}
          ${this.hasIcon
            ? html`<ix-icon
                class="icon"
                data-testid=${TestIds.ICON(this.node.id)}
                >${icon}</ix-icon
              >`
            : nothing}
          <label
            data-testid="${TestIds.LABEL(this.node.id)}"
            class="${this.allowMultiLine
              ? 'label-multi-line'
              : 'label-single-line'}"
            >${this.node.label}</label
          >
        </div>
      </div>
      ${hasChildren && this.isExpanded
        ? html`<div role="group"><slot></slot></div>`
        : html`<slot></slot>`}
    </div>`;
  }

  protected updated(): void {
    if (this.scrollToActive && !this._hasScrolledToActive) {
      const el = this.shadowRoot?.querySelector('[active-tree-node]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this._hasScrolledToActive = true;
    }
    if (this.isSelected) {
      this.dispatchEvent(
        new CustomEvent('selected-node-ref', {
          detail: this._treeNodeContainer,
          bubbles: true,
          composed: true,
        })
      );
    }
  }
}
